import { isAxiosError } from 'axios';

function fieldErrors(value: unknown, path: string): string[] {
  const label = (text: string) =>
    !path || path === 'non_field_errors' ? text : `${path}: ${text}`;

  if (typeof value === 'string') return value ? [label(value)] : [];
  if (Array.isArray(value)) {
    const texts = value.filter((v): v is string => typeof v === 'string');
    if (texts.length) return [label(texts.join(', '))];
    return value.flatMap((v) => fieldErrors(v, path));
  }
  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).flatMap(
      ([key, v]) => fieldErrors(v, path ? `${path}.${key}` : key)
    );
  }
  return [];
}

/** An axios timeout (glp `lib/useQueryHooks/Orders/orderRequest.ts`). */
export function isTimeoutError(error: unknown): boolean {
  const code = (error as { code?: string } | null)?.code;
  return (
    isAxiosError(error) && (code === 'ECONNABORTED' || code === 'ETIMEDOUT')
  );
}

function messageFromBody(data: unknown): string {
  if (typeof data === 'string') return data.trim();
  if (!data || typeof data !== 'object') return '';

  const body = data as Record<string, unknown>;

  if (typeof body.error === 'string' && body.error) {
    const bracket = body.error.match(/\['(.+?)'\]/);
    if (bracket?.[1]) return bracket[1];
    const afterColon = body.error.match(/:\s*(.+)$/);
    if (afterColon?.[1]) return afterColon[1].trim();
    return body.error;
  }
  if (typeof body.detail === 'string' && body.detail) return body.detail;
  if (typeof body.message === 'string' && body.message) return body.message;

  return fieldErrors(body, '').join('; ');
}

/**
 * A 400's DRF field errors for the named form fields (`{phone: ["…"]}`), one
 * line per field, so a form can show each next to its input. Anything else
 * (another status, a string body, a field the form does not have) is left to
 * `getApiErrorMessage`.
 */
export function apiFieldErrors<K extends string>(
  error: unknown,
  fields: readonly K[]
): Partial<Record<K, string>> {
  if (!isAxiosError(error) || error.response?.status !== 400) return {};
  const body = error.response.data;
  if (!body || typeof body !== 'object' || Array.isArray(body)) return {};
  const out: Partial<Record<K, string>> = {};
  fields.forEach((field) => {
    const value = (body as Record<string, unknown>)[field];
    let text = '';
    if (typeof value === 'string') text = value;
    else if (Array.isArray(value)) {
      text = value.filter((v): v is string => typeof v === 'string').join(' ');
    }
    if (text) out[field] = text;
  });
  return out;
}

/** HTTP status of a failed axios request, if it got an answer. */
export function apiErrorStatus(error: unknown): number | undefined {
  return isAxiosError(error) ? error.response?.status : undefined;
}

/**
 * The message to show the user for a failed request.
 *
 * 4xx bodies are read the way glp-frontend reads them
 * (`apps/glp-frontend/src/utils/errors/parseApiError.ts`): `error`, then DRF's
 * `detail`, then `message`, then DRF field errors as "field: messages".
 * A 5xx, a network failure or a timeout gets `fallback` — the body of a server
 * error is not something the user can act on (it can be a traceback).
 * A plain `Error` (e.g. a failed file upload before the request) keeps its
 * own message.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    if (!error.response || !status || status >= 500) return fallback;
    return messageFromBody(error.response.data) || fallback;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}
